import { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { api } from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';

interface ForumsPageProps {
  onNavigate: (page: string, data?: any) => void;
  user?: any;
  view?: 'categories' | 'category' | 'thread';
  categorySlug?: string;
  threadId?: number;
}

const CATEGORY_ICONS: Record<string, string> = {
  'general-discussion': '💬',
  'genre-talk': '📚',
  'writing-advice': '✍️',
  'review-swaps': '🔄',
  'site-feedback': '🛠️',
  'introductions': '👋',
};

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, now - then);
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function ForumsPage({ onNavigate, user, view = 'categories', categorySlug, threadId }: ForumsPageProps) {
  if (view === 'thread' && threadId) {
    return <ThreadView threadId={threadId} user={user} onNavigate={onNavigate} />;
  }
  if (view === 'category' && categorySlug) {
    return <CategoryView slug={categorySlug} user={user} onNavigate={onNavigate} />;
  }
  return <CategoriesView user={user} onNavigate={onNavigate} />;
}

function CategoriesView({ onNavigate }: { user?: any; onNavigate: (page: string, data?: any) => void }) {
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.forums.categories()
      .then(setCategories)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Community Forums</h1>
        <p className="text-gray-400">Connect with fellow readers and writers</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : categories.length === 0 ? (
        <div className="py-16">
          <div className="text-center mb-10">
            <div className="w-20 h-20 rounded-full bg-ink-900/30 border border-ink-700/30 flex items-center justify-center text-4xl mx-auto mb-4">
              💬
            </div>
            <h2 className="text-2xl font-bold mb-2">Forums Coming Soon</h2>
            <p className="text-gray-400 max-w-md mx-auto">
              Our community forums are being set up. Soon you'll be able to discuss genres, swap reviews, get writing advice, and connect with fellow authors.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
            {[
              { icon: '✍️', label: 'Writing Advice', desc: 'Get tips from experienced authors' },
              { icon: '🔄', label: 'Review Swaps', desc: 'Trade honest reviews with peers' },
              { icon: '📚', label: 'Genre Talk', desc: 'Discuss your favourite genres' },
            ].map(item => (
              <div key={item.label} className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
                <span className="text-2xl block mb-2">{item.icon}</span>
                <h3 className="font-semibold text-sm mb-1">{item.label}</h3>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {categories.map((cat: any) => (
            <button
              key={cat.id}
              onClick={() => onNavigate('forum-category', { slug: cat.slug })}
              className="bg-gray-900 border border-gray-800 rounded-xl p-5 text-left hover:border-gray-700 hover:bg-gray-900/80 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="text-3xl flex-shrink-0">
                  {cat.icon || CATEGORY_ICONS[cat.slug] || '📁'}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold group-hover:text-ink-400 transition-colors">{cat.name}</h3>
                  {cat.description && (
                    <p className="text-sm text-gray-400 mt-1 line-clamp-2">{cat.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                    <span>{cat._count?.threads ?? cat.threadCount ?? 0} threads</span>
                    {cat.lastThread && (
                      <span className="truncate">
                        Latest: <span className="text-gray-400">{cat.lastThread.title}</span>
                        {' · '}{relativeTime(cat.lastThread.lastPostAt || cat.lastThread.createdAt)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryView({ slug, user, onNavigate }: { slug: string; user?: any; onNavigate: (page: string, data?: any) => void }) {
  const [category, setCategory] = useState<any>(null);
  const [threads, setThreads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState<'newest' | 'active' | 'views'>('active');
  const [showNewThread, setShowNewThread] = useState(false);
  const [threadTitle, setThreadTitle] = useState('');
  const [threadBody, setThreadBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.forums.category(slug, { page });
      setCategory(data.category || data);
      setThreads(data.threads || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to load category:', err);
    } finally {
      setLoading(false);
    }
  }, [slug, page]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const sortedThreads = [...threads].sort((a, b) => {
    const pinnedDiff = (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0);
    if (pinnedDiff !== 0) return pinnedDiff;
    if (sortBy === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    if (sortBy === 'views') return (b.viewCount || 0) - (a.viewCount || 0);
    return new Date(b.lastPostAt || b.createdAt).getTime() - new Date(a.lastPostAt || a.createdAt).getTime();
  });

  const handleCreateThread = async () => {
    if (!category || !threadTitle.trim() || !threadBody.trim()) return;
    setSubmitting(true);
    try {
      const thread = await api.forums.createThread({
        categoryId: category.id,
        title: threadTitle.trim(),
        body: threadBody,
      });
      setShowNewThread(false);
      setThreadTitle('');
      setThreadBody('');
      onNavigate('forum-thread', { id: thread.id });
    } catch (err) {
      console.error('Failed to create thread:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <button onClick={() => onNavigate('forums')} className="hover:text-ink-400 transition-colors">Forums</button>
        <span>/</span>
        <span className="text-gray-300">{category?.name || slug}</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <span>{category?.icon || CATEGORY_ICONS[slug] || '📁'}</span>
            {category?.name || slug}
          </h1>
          {category?.description && (
            <p className="text-gray-400 mt-1">{category.description}</p>
          )}
        </div>
        {user && (
          <button
            onClick={() => setShowNewThread(!showNewThread)}
            className="px-5 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
          >
            + New Thread
          </button>
        )}
      </div>

      {showNewThread && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Create New Thread</h3>
          <input
            value={threadTitle}
            onChange={e => setThreadTitle(e.target.value)}
            placeholder="Thread title"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 mb-4 focus:border-ink-500 focus:outline-none"
          />
          <RichTextEditor
            value={threadBody}
            onChange={setThreadBody}
            placeholder="Write your post..."
            minHeight="200px"
          />
          <div className="flex gap-3 justify-end mt-4">
            <button
              onClick={() => { setShowNewThread(false); setThreadTitle(''); setThreadBody(''); }}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateThread}
              disabled={!threadTitle.trim() || !threadBody.trim() || submitting}
              className="px-6 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg font-medium disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Posting...' : 'Create Thread'}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        {(['active', 'newest', 'views'] as const).map(s => (
          <button
            key={s}
            onClick={() => setSortBy(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              sortBy === s ? 'bg-ink-600/20 text-ink-400' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
          >
            {s === 'active' ? 'Most Active' : s === 'newest' ? 'Newest' : 'Most Views'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : sortedThreads.length === 0 ? (
        <div className="text-center py-16 bg-gray-900/50 rounded-2xl border border-gray-800">
          <span className="text-4xl block mb-3">🗨️</span>
          <h2 className="text-xl font-bold mb-2">No threads yet</h2>
          <p className="text-gray-400 mb-6">Be the first to start a conversation in this category!</p>
          {user ? (
            <button
              onClick={() => setShowNewThread(true)}
              className="px-5 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors text-sm"
            >
              + Start the First Thread
            </button>
          ) : (
            <button
              onClick={() => onNavigate('login')}
              className="px-5 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors text-sm"
            >
              Sign In to Post
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {sortedThreads.map(thread => (
            <button
              key={thread.id}
              onClick={() => onNavigate('forum-thread', { id: thread.id })}
              className="w-full bg-gray-900 border border-gray-800 rounded-xl p-4 text-left hover:border-gray-700 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {thread.isPinned && (
                      <span className="px-1.5 py-0.5 bg-amber-900/30 text-amber-400 rounded text-xs font-medium">Pinned</span>
                    )}
                    {thread.isLocked && (
                      <span className="px-1.5 py-0.5 bg-red-900/30 text-red-400 rounded text-xs font-medium">Locked</span>
                    )}
                    <h3 className="font-semibold group-hover:text-ink-400 transition-colors truncate">{thread.title}</h3>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="text-gray-400">{thread.user?.username || 'Unknown'}</span>
                    <span>{relativeTime(thread.createdAt)}</span>
                    <span>{thread._count?.posts ?? thread.replyCount ?? 0} replies</span>
                    <span>{thread.viewCount || 0} views</span>
                  </div>
                </div>
                {thread.lastPostAt && (
                  <div className="text-xs text-gray-500 flex-shrink-0 text-right">
                    <div>Last post</div>
                    <div className="text-gray-400">{relativeTime(thread.lastPostAt)}</div>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm disabled:opacity-50 transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-gray-400 px-3">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm disabled:opacity-50 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function ThreadView({ threadId, user, onNavigate }: { threadId: number; user?: any; onNavigate: (page: string, data?: any) => void }) {
  const [thread, setThread] = useState<any>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [replyBody, setReplyBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState('');

  const fetchThread = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.forums.thread(threadId, { page });
      setThread(data.thread || data);
      setPosts(data.posts || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to load thread:', err);
    } finally {
      setLoading(false);
    }
  }, [threadId, page]);

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  const handleReply = async () => {
    if (!replyBody.trim()) return;
    setSubmitting(true);
    try {
      await api.forums.createPost({ threadId, body: replyBody });
      setReplyBody('');
      await fetchThread();
    } catch (err) {
      console.error('Failed to post reply:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPost = async (postId: number) => {
    if (!editBody.trim()) return;
    setSubmitting(true);
    try {
      await api.forums.updatePost(postId, { body: editBody });
      setEditingPostId(null);
      setEditBody('');
      await fetchThread();
    } catch (err) {
      console.error('Failed to edit post:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async (postId: number) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await api.forums.deletePost(postId);
      await fetchThread();
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  const handleTogglePin = async () => {
    if (!thread) return;
    try {
      await api.forums.updateThread(thread.id, { isPinned: !thread.isPinned });
      setThread((t: any) => ({ ...t, isPinned: !t.isPinned }));
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };

  const handleToggleLock = async () => {
    if (!thread) return;
    try {
      await api.forums.updateThread(thread.id, { isLocked: !thread.isLocked });
      setThread((t: any) => ({ ...t, isLocked: !t.isLocked }));
    } catch (err) {
      console.error('Failed to toggle lock:', err);
    }
  };

  if (loading && !thread) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <span className="text-5xl block mb-4">🔍</span>
        <h2 className="text-2xl font-bold mb-2">Thread not found</h2>
        <button onClick={() => onNavigate('forums')} className="text-ink-400 hover:text-ink-300 mt-4">
          Back to Forums
        </button>
      </div>
    );
  }

  const categorySlug = thread.category?.slug || '';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <button onClick={() => onNavigate('forums')} className="hover:text-ink-400 transition-colors">Forums</button>
        <span>/</span>
        <button
          onClick={() => onNavigate('forum-category', { slug: categorySlug })}
          className="hover:text-ink-400 transition-colors"
        >
          {thread.category?.name || 'Category'}
        </button>
        <span>/</span>
        <span className="text-gray-300 truncate max-w-xs">{thread.title}</span>
      </div>

      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {thread.isPinned && (
                <span className="px-2 py-0.5 bg-amber-900/30 text-amber-400 rounded text-xs font-medium">Pinned</span>
              )}
              {thread.isLocked && (
                <span className="px-2 py-0.5 bg-red-900/30 text-red-400 rounded text-xs font-medium">Locked</span>
              )}
              <h1 className="text-2xl font-bold">{thread.title}</h1>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span>Started by <span className="text-gray-300">{thread.user?.username || 'Unknown'}</span></span>
              <span>{relativeTime(thread.createdAt)}</span>
              <span>{thread.viewCount || 0} views</span>
            </div>
          </div>
          {user?.role === 'ADMIN' && (
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={handleTogglePin}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  thread.isPinned ? 'bg-amber-900/30 text-amber-400 hover:bg-amber-900/50' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {thread.isPinned ? 'Unpin' : 'Pin'}
              </button>
              <button
                onClick={handleToggleLock}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  thread.isLocked ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {thread.isLocked ? 'Unlock' : 'Lock'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {posts.map((post, idx) => {
          const isOp = idx === 0 && page === 1;
          const isAuthor = user && post.userId === user.id;
          const isAdmin = user?.role === 'ADMIN';
          const canEdit = isAuthor;
          const canDelete = isAuthor || isAdmin;

          return (
            <div
              key={post.id}
              className={`bg-gray-900 border rounded-xl overflow-hidden ${
                isOp ? 'border-ink-500/30' : 'border-gray-800'
              }`}
            >
              <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-800 bg-gray-900/50">
                <div className="w-8 h-8 rounded-full bg-ink-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {(post.user?.username || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => post.user?.username && onNavigate('profile', { username: post.user.username })}
                    className="text-sm font-semibold text-gray-200 hover:text-ink-400 transition-colors"
                  >
                    {post.user?.username || 'Unknown'}
                  </button>
                  {isOp && <span className="ml-2 text-xs text-ink-400 font-medium">OP</span>}
                </div>
                <span className="text-xs text-gray-500">{relativeTime(post.createdAt)}</span>
                {post.updatedAt && post.updatedAt !== post.createdAt && (
                  <span className="text-xs text-gray-600">(edited)</span>
                )}
              </div>

              {editingPostId === post.id ? (
                <div className="p-5">
                  <RichTextEditor
                    value={editBody}
                    onChange={setEditBody}
                    placeholder="Edit your post..."
                    minHeight="150px"
                  />
                  <div className="flex gap-2 justify-end mt-3">
                    <button
                      onClick={() => { setEditingPostId(null); setEditBody(''); }}
                      className="px-4 py-2 text-gray-400 hover:text-white text-sm transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleEditPost(post.id)}
                      disabled={!editBody.trim() || submitting}
                      className="px-5 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                    >
                      {submitting ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-5 py-4">
                  <div
                    className="prose prose-invert max-w-none text-gray-300 text-sm leading-relaxed
                      [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2
                      [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1
                      [&_blockquote]:border-l-4 [&_blockquote]:border-ink-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-400
                      [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6
                      [&_p]:mb-2"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body, { ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 's', 'em', 'strong', 'h2', 'h3', 'blockquote', 'ul', 'ol', 'li', 'hr', 'div', 'span', 'a'], ALLOWED_ATTR: ['style', 'class', 'href', 'rel', 'target'] }) }}
                  />
                  {(canEdit || canDelete) && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-800">
                      {canEdit && (
                        <button
                          onClick={() => { setEditingPostId(post.id); setEditBody(post.body); }}
                          className="text-xs text-gray-500 hover:text-ink-400 transition-colors"
                        >
                          Edit
                        </button>
                      )}
                      {canDelete && !isOp && (
                        <button
                          onClick={() => handleDeletePost(post.id)}
                          className="text-xs text-gray-500 hover:text-red-400 transition-colors"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm disabled:opacity-50 transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-gray-400 px-3">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm disabled:opacity-50 transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {!thread.isLocked && user ? (
        <div className="mt-8 bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="font-semibold mb-3">Reply</h3>
          <RichTextEditor
            value={replyBody}
            onChange={setReplyBody}
            placeholder="Write your reply..."
            minHeight="150px"
          />
          <div className="flex justify-end mt-3">
            <button
              onClick={handleReply}
              disabled={!replyBody.trim() || submitting}
              className="px-6 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-lg font-semibold disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Posting...' : 'Post Reply'}
            </button>
          </div>
        </div>
      ) : thread.isLocked ? (
        <div className="mt-8 text-center py-6 bg-gray-900/50 rounded-xl border border-gray-800">
          <span className="text-2xl block mb-2">🔒</span>
          <p className="text-gray-400 text-sm">This thread is locked. No new replies can be posted.</p>
        </div>
      ) : (
        <div className="mt-8 text-center py-6 bg-gray-900/50 rounded-xl border border-gray-800">
          <p className="text-gray-400 text-sm">
            <button onClick={() => onNavigate('login')} className="text-ink-400 hover:text-ink-300">Sign in</button>
            {' '}to reply to this thread.
          </p>
        </div>
      )}
    </div>
  );
}
